import React, { useState } from "react";
import { Heart, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useWishlist } from "../lib/wishlist";
import { formatApiError } from "../lib/api";
import { requireAuth } from "./SignInGate";

/**
 * Size: "sm" (icon-only overlay on cards) | "lg" (pill button for detail pages)
 * ownerId is the listing seller id - if it equals current user id, button is hidden.
 * v79.181 — Item 24: `category === "jobs"` swaps the verb to "Shortlist"
 * with a bookmark icon so employers/candidates use a job-appropriate
 * label. The underlying wishlist API is reused; only the presentation
 * changes.
 */
export default function WishlistButton({ listingId, ownerId, size = "sm", category }) {
    const { user } = useAuth();
    const { isWishlisted, toggle } = useWishlist();
    const [busy, setBusy] = useState(false);

    if (user && ownerId && user.id === ownerId) return null;

    const saved = isWishlisted(listingId);
    const isJob = category === "jobs";
    const Icon = isJob ? Bookmark : Heart;
    const verb = isJob
        ? (saved ? "Shortlisted" : "Shortlist")
        : (saved ? "Saved in wishlist" : "Save to wishlist");
    const ariaLabel = isJob
        ? (saved ? "Remove from shortlist" : "Add to shortlist")
        : (saved ? "Remove from wishlist" : "Add to wishlist");

    const click = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) {
            // v78.59 — inline modal instead of yanking the user to /login.
            const ok = await requireAuth(isJob ? "Sign in to shortlist" : "Sign in to save listings");
            if (!ok) return;
        }
        if (busy) return;
        setBusy(true);
        try {
            const nowSaved = await toggle(listingId);
            toast.success(nowSaved
                ? (isJob ? "Added to your shortlist" : "Saved to wishlist")
                : (isJob ? "Removed from shortlist" : "Removed from wishlist"));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (size === "lg") {
        return (
            <button
                data-testid={`wishlist-lg-${listingId}`}
                onClick={click}
                disabled={busy}
                aria-pressed={saved}
                className={`w-full h-11 rounded-full flex items-center justify-center gap-2 font-semibold border transition-colors ${saved
                    ? (isJob
                        ? "bg-aduku-yellow/20 text-aduku-yellow border-aduku-yellow/50 hover:bg-aduku-yellow/30"
                        : "bg-red-500/15 text-red-500 border-red-500/40 hover:bg-red-500/25")
                    : "bg-white/5 text-white border-white/15 hover:bg-white/15"
                    }`}
            >
                <Icon className={`w-4 h-4 ${saved && !isJob ? "fill-red-500" : ""} ${saved && isJob ? "fill-aduku-yellow" : ""}`} />
                {verb}
            </button>
        );
    }

    return (
        <button
            data-testid={`wishlist-sm-${listingId}`}
            onClick={click}
            disabled={busy}
            aria-pressed={saved}
            aria-label={ariaLabel}
            title={verb}
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md backdrop-blur-md transition-colors ${saved
                ? (isJob ? "bg-aduku-yellow text-aduku-navy" : "bg-red-500/90 text-white")
                : "bg-white/80 text-aduku-navy hover:bg-white hover:text-red-500"
                } ${busy ? "opacity-60" : ""}`}
        >
            <Icon className={`w-4 h-4 ${saved ? "fill-current" : ""}`} />
        </button>
    );
}
